import { supabase } from "@/integrations/supabase/client";
import { Voice } from "@/types/voice/voice";

const VOICES_STORAGE_KEY = "elevenlabs_voices_cache";
const VOICES_STORAGE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours

interface VoicesCacheEntry {
  voices: Voice[];
  timestamp: number;
}

export class VoiceService {
  private static instance: VoiceService;

  private constructor() {
    // Stateless service - caching handled by React Query
  }

  public static getInstance(): VoiceService {
    if (!VoiceService.instance) {
      VoiceService.instance = new VoiceService();
    }
    return VoiceService.instance;
  }

  /**
   * Get recommended voices - delegates to getAllVoices with optional filters and pagination
   */
  public async getRecommendedVoices(options?: {
    page?: number;
    pageSize?: number;
    search?: string;
    category?: string;
    accent?: string;
    language?: string;
    age?: string;
    gender?: string;
  }): Promise<{
    voices: Voice[];
    totalCount: number;
    hasMore: boolean;
    page: number;
    pageSize: number;
  }> {
    return this.getAllVoices(options);
  }

  /**
   * Get all available voices with optional filters
   * Caching is handled by React Query at the hook level
   */
  public async getAllVoices(options?: {
    page?: number;
    pageSize?: number;
    category?: string;
    accent?: string;
    language?: string;
    age?: string;
    gender?: string;
    search?: string;
  }): Promise<{
    voices: Voice[];
    totalCount: number;
    hasMore: boolean;
    page: number;
    pageSize: number;
  }> {
    try {
      const pageSize = options?.pageSize || 100;
      const page = options?.page || 0;

      const params: Record<string, any> = {
        page,
        page_size: pageSize,
      };

      // Add optional filters if provided and not 'all'
      if (options?.search) {
        params.search = options.search;
      }
      if (options?.category && options.category !== "all") {
        params.category = options.category;
      }
      if (options?.accent && options.accent !== "all") {
        params.accent = options.accent;
      }
      if (options?.language && options.language !== "all") {
        params.language = options.language;
      }
      if (options?.age && options.age !== "all") {
        params.age = options.age;
      }
      if (options?.gender && options.gender !== "all") {
        params.gender = options.gender;
      }

      console.log(
        `Fetching all voices from gateway - page: ${page}, pageSize: ${pageSize}, filters:`,
        params,
      );
      const { data, error } = await supabase.functions.invoke("elevenlabs", {
        body: {
          resource: "voices",
          action: "list",
          params,
        },
      });

      if (error) {
        console.warn("Gateway failed:", error);
        return { voices: [], totalCount: 0, hasMore: false, page: 0, pageSize: 100 };
      }

      const responseData = data.data || data;
      const rawVoices = responseData.voices || [];
      const voices = this.normalizeVoices(rawVoices);
      const totalCount = responseData.total_count || voices.length;
      const hasMore =
        responseData.has_more !== undefined ? responseData.has_more : voices.length === pageSize;

      console.log(
        `Gateway returned ${voices.length} voices (total: ${totalCount}, hasMore: ${hasMore})`,
      );

      return {
        voices,
        totalCount,
        hasMore,
        page,
        pageSize,
      };
    } catch (error) {
      console.error("Failed to fetch voices:", error);
      return { voices: [], totalCount: 0, hasMore: false, page: 0, pageSize: 100 };
    }
  }

  /**
   * Get shared/community voices with optional filters
   * Fetches from https://api.elevenlabs.io/v1/shared-voices
   * Caching is handled by React Query at the hook level
   */
  public async getSharedVoices(options?: {
    page?: number;
    pageSize?: number;
    search?: string;
    category?: string;
    accent?: string;
    language?: string;
    age?: string;
    gender?: string;
  }): Promise<{
    voices: Voice[];
    totalCount: number;
    hasMore: boolean;
    page: number;
    pageSize: number;
  }> {
    try {
      const pageSize = options?.pageSize || 100;
      const page = options?.page || 0;

      const params: Record<string, any> = {
        page,
        page_size: pageSize,
      };

      // Add optional filters if provided and not 'all'
      if (options?.search) {
        params.search = options.search;
      }
      if (options?.category && options.category !== "all") {
        params.category = options.category;
      }
      if (options?.accent && options.accent !== "all") {
        params.accent = options.accent;
      }
      if (options?.language && options.language !== "all") {
        params.language = options.language;
      }
      if (options?.age && options.age !== "all") {
        params.age = options.age;
      }
      if (options?.gender && options.gender !== "all") {
        params.gender = options.gender;
      }

      console.log(
        `Fetching shared voices from gateway - page: ${page}, pageSize: ${pageSize}, filters:`,
        params,
      );
      const { data, error } = await supabase.functions.invoke("elevenlabs", {
        body: {
          resource: "voices",
          action: "list-shared",
          params,
        },
      });

      if (error) {
        console.warn("Gateway failed:", error);
        return { voices: [], totalCount: 0, hasMore: false, page: 0, pageSize: 100 };
      }

      const responseData = data.data || data;
      const rawVoices = responseData.voices || [];
      const voices = this.normalizeVoices(rawVoices);
      const totalCount = responseData.total_count || voices.length;
      const hasMore =
        responseData.has_more !== undefined ? responseData.has_more : voices.length === pageSize;

      console.log(
        `Gateway returned ${voices.length} shared voices (total: ${totalCount}, hasMore: ${hasMore})`,
      );

      return {
        voices,
        totalCount,
        hasMore,
        page,
        pageSize,
      };
    } catch (error) {
      console.error("Failed to fetch shared voices:", error);
      return { voices: [], totalCount: 0, hasMore: false, page: 0, pageSize: 100 };
    }
  }

  /**
   * Get voice by ID from ElevenLabs API with full details
   * Returns complete voice data including fine-tuning, sharing, and verification info
   */
  public async getVoiceById(voiceId: string): Promise<Voice | null> {
    try {
      // Check cache first
      // const cachedVoice = this.getVoiceFromCache(voiceId);
      // if (cachedVoice) {
      //   return cachedVoice;
      // }

      console.log(`Fetching voice details for ID: ${voiceId}`);
      const { data, error } = await supabase.functions.invoke("elevenlabs", {
        body: {
          resource: "voices",
          action: "get",
          params: {
            voice_id: voiceId,
          },
        },
      });

      if (error) {
        console.error("Failed to fetch voice details:", error);
        throw new Error(`Voice ${voiceId} not found or no longer available`);
      }

      const rawVoice = data.data || data;
      if (!rawVoice || !rawVoice.voice_id) {
        console.warn("No voice data returned from API");
        throw new Error(`Voice ${voiceId} not found or no longer available`);
      }

      const voice = this.normalizeVoices([rawVoice])[0];
      console.log(`Fetched voice details for: ${voice.name}`, voice);

      // Cache the voice
      this.cacheVoices([voice]);

      return voice;
    } catch (error) {
      console.error("Failed to fetch voice details:", error);
      throw error;
    }
  }

  /**
   * Get voice by ID from local cache
   * Returns null if voice not found in cache
   */
  public getVoiceFromCache(voiceId: string): Voice | null {
    const cached = this.getCachedVoices();
    return cached.find((voice) => voice.id === voiceId) || null;
  }

  /**
   * Add a shared voice to user's library
   * Accepts a voice object and passes it to the gateway
   * Checks if voice already exists in collection before adding
   */
  public async addVoice(voice: Voice): Promise<Voice | null> {
    try {
      console.log(`Adding voice to library: ${voice.name}`);

      const { data, error } = await supabase.functions.invoke("elevenlabs", {
        body: {
          resource: "voices",
          action: "add",
          params: {
            voice: voice.raw,
          },
        },
      });

      if (error) {
        console.warn("Failed to add voice:", error);
        return null;
      }

      console.log(`Successfully added voice:`, data);

      const voiceData = data.data || data;
      return this.normalizeVoices([voiceData])[0];
    } catch (error) {
      console.error("Failed to add voice:", error);
      return null;
    }
  }

  // Private helper methods

  private normalizeVoices(rawVoices: any[]): Voice[] {
    return rawVoices.map((voice) => ({
      id: voice.voice_id || voice.id,
      name: voice.name,
      description: voice.description,
      preview_url: voice.preview_url,
      category: voice.category,
      featured: voice.featured,
      labels: voice.labels
        ? {
            accent: voice.labels.accent || voice.accent,
            descriptive: voice.labels.descriptive || voice.descriptive,
            age: voice.labels.age || voice.age,
            gender: voice.labels.gender || voice.gender,
            use_case: voice.labels.use_case || voice.use_case,
            language: voice.labels.language || voice.language,
          }
        : {
            accent: voice.accent,
            descriptive: voice.descriptive,
            age: voice.age,
            gender: voice.gender,
            use_case: voice.use_case,
            language: voice.language,
          },
      verified_languages: voice.verified_languages,
      raw: voice,
    }));
  }

  public cacheVoices(voices: Voice[]): void {
    try {
      const cached = this.getCachedVoices();
      const voiceMap = new Map<string, Voice>();

      // Keep existing cached voices
      cached.forEach((voice) => voiceMap.set(voice.id, voice));

      // Add/update new voices
      voices.forEach((voice) => voiceMap.set(voice.id, voice));

      const cacheEntry: VoicesCacheEntry = {
        voices: Array.from(voiceMap.values()),
        timestamp: Date.now(),
      };

      localStorage.setItem(VOICES_STORAGE_KEY, JSON.stringify(cacheEntry));
    } catch (error) {
      console.warn("Failed to cache voices:", error);
    }
  }

  private getCachedVoices(): Voice[] {
    try {
      const cached = localStorage.getItem(VOICES_STORAGE_KEY);
      if (!cached) return [];

      const entry: VoicesCacheEntry = JSON.parse(cached);
      const isExpired = Date.now() - entry.timestamp > VOICES_STORAGE_EXPIRY;

      if (isExpired) {
        localStorage.removeItem(VOICES_STORAGE_KEY);
        return [];
      }

      return entry.voices || [];
    } catch (error) {
      console.warn("Failed to retrieve cached voices:", error);
      return [];
    }
  }
}
